import {
  type ConversationId,
  type ConversationThreadId,
  type DraftMessageId,
  DraftMessageId as DraftMessageIdSchema,
  type MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  type MongoUserId,
  MongoUserId as MongoUserIdSchema,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SaveDraftInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { toast } from "@redotech/redo-web/alert";
import { and, eq, isUndefined, useLiveQuery } from "@tanstack/react-db";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "effect";
import { useCallback, useContext, useRef, useState } from "react";
import type { DraftCollectionItem } from "../db/collections";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import { conversationCountsQueryKey } from "./use-conversation-counts";

interface UseDraftOptions {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId?: MessageId;
  enabled?: boolean;
}

/**
 * Hook to fetch the current draft for a conversation thread context.
 * Uses the TanStack DB drafts collection instead of React Query directly.
 */
export function useDraft({
  conversationId,
  threadId,
  inReplyToMessageId,
}: UseDraftOptions) {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          inReplyToMessageId
            ? and(
                eq(draft.conversationId, String(conversationId)),
                eq(draft.threadId, String(threadId)),
                eq(draft.inReplyToMessageId, String(inReplyToMessageId)),
              )
            : and(
                eq(draft.conversationId, String(conversationId)),
                eq(draft.threadId, String(threadId)),
                isUndefined(draft.inReplyToMessageId),
              ),
        )
        .findOne(),
    [conversationId, threadId, inReplyToMessageId],
  );

  return { data: result.data ?? null };
}

interface UseDraftMutationsOptions {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId?: MessageId;
  existingDraftId?: DraftMessageId | null;
  editorMode: "email" | "chat";
}

/**
 * Builds a temp DraftCollectionItem for non-optimistic inserts.
 * The item is never displayed — it only carries fields needed by onInsert
 * to make the saveDraft API call. Placeholder values are used for fields
 * that only exist on server-returned drafts (id, timestamps).
 */
function buildTempDraftItem(
  conversationId: ConversationId,
  threadId: ConversationThreadId,
  senderMongoUserId: MongoUserId,
  inReplyToMessageId: MessageId | undefined,
  overrides: Partial<
    Pick<
      DraftCollectionItem,
      "content" | "plainContent" | "attachments" | "channel" | "channelData"
    >
  >,
): DraftCollectionItem {
  const now = DateTime.unsafeMake(new Date());
  return {
    id: DraftMessageIdSchema.generate(),
    conversationId,
    threadId,
    inReplyToMessageId: inReplyToMessageId
      ? String(inReplyToMessageId)
      : undefined,
    content:
      overrides.content ??
      JSON.stringify([{ type: "p", children: [{ text: "" }] }]),
    plainContent: overrides.plainContent ?? null,
    attachments: overrides.attachments ?? [],
    senderMongoUserId,
    senderType: "merchant",
    channel: overrides.channel ?? "redoChat",
    channelData: overrides.channelData ?? null,
    status: "PENDING",
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Hook to manage draft mutations using the TanStack DB collection's
 * onInsert / onUpdate / onDelete callbacks (defined in collections.ts).
 */
export function useDraftMutations({
  conversationId,
  threadId,
  inReplyToMessageId,
  existingDraftId,
  editorMode,
}: UseDraftMutationsOptions) {
  const collections = useGlobalConversationCollections();
  const queryClient = useQueryClient();
  const user = useContext(UserContext);
  const senderMongoUserId = user
    ? MongoUserIdSchema.fromString(user._id)
    : MongoUserIdSchema.makeNew();
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const invalidateConversationCounts = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: conversationCountsQueryKey,
    });
  }, [queryClient]);

  const incrementConversationDrafts = useCallback(
    (delta: number) => {
      const conversation =
        collections.conversationsList.collection.get(conversationId);
      if (conversation) {
        collections.conversationsList.collection.update(conversationId, (c) => {
          c.numDrafts = Math.max(0, c.numDrafts + delta);
        });
      }
    },
    [collections.conversationsList.collection, conversationId],
  );

  const saveDraft = useCallback(
    async (input: SaveDraftInput) => {
      const isCreatingNewDraft = !existingDraftId;
      const savePromise = (async () => {
        setIsSavingDraft(true);
        try {
          if (existingDraftId) {
            const tx = collections.drafts.update(
              String(existingDraftId),
              (d) => {
                d.content = input.content;
                d.plainContent = input.plainContent ?? null;
                d.attachments = [...input.attachments];
              },
            );
            await tx.isPersisted.promise;
          } else {
            const tempItem = buildTempDraftItem(
              conversationId,
              input.threadId,
              senderMongoUserId,
              input.inReplyToMessageId,
              {
                content: input.content,
                plainContent: input.plainContent ?? null,
                attachments: [...input.attachments],
                channel: input.channel,
                channelData: input.channelData,
              },
            );
            const tx = collections.drafts.insert(tempItem, {
              optimistic: false,
            });
            await tx.isPersisted.promise;
          }
          if (isCreatingNewDraft) {
            incrementConversationDrafts(1);
            invalidateConversationCounts();
          }
        } finally {
          setIsSavingDraft(false);
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      return savePromise;
    },
    [
      collections.drafts,
      conversationId,
      existingDraftId,
      senderMongoUserId,
      incrementConversationDrafts,
      invalidateConversationCounts,
    ],
  );

  const deleteDraft = useCallback(
    async (draftId: DraftMessageId) => {
      setIsDeletingDraft(true);
      try {
        const tx = collections.drafts.delete(String(draftId));
        await tx.isPersisted.promise;
        toast("Draft deleted", { variant: "success" });
        incrementConversationDrafts(-1);
        invalidateConversationCounts();
      } catch {
        toast("Failed to delete draft", { variant: "error" });
      } finally {
        setIsDeletingDraft(false);
      }
    },
    [
      collections.drafts,
      incrementConversationDrafts,
      invalidateConversationCounts,
    ],
  );

  const waitForPendingSave = useCallback(async () => {
    if (pendingSaveRef.current) {
      await pendingSaveRef.current;
    }
  }, []);

  return {
    saveDraft,
    isSavingDraft,
    deleteDraft,
    isDeletingDraft,
    waitForPendingSave,
  };
}
